import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { IconButton, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormAddDocumentSchema } from 'utils/validate';
import { InputText } from 'app/components/elements/InputText';
import { ButtonCustom, ButtonOutLine } from 'app/components/elements/ButtonCustom';
import { SelectChangeEvent } from '@mui/material/Select';
import { InputSelect, SelectOptions } from 'app/components/elements/InputSelect';
import { InputSelectMultiple } from 'app/components/elements/InputSelectMultiple';
import { FormAddDocument } from 'types/documents';
import { TextError } from 'app/components/elements/TextError';
type ModalAddDocumentProps = {
  isOpen: boolean;
  onCloseModal: () => void;
  onSubmit: (value: FormAddDocument) => void;
  optionClient?: SelectOptions[];
  category?: SelectOptions[];
  clientId?: string;
};
export const ModalAddDocument = ({
  isOpen,
  onCloseModal,
  onSubmit,
  optionClient,
  category
}: ModalAddDocumentProps) => {
  const {
    handleSubmit,
    register,
    reset,
    watch,
    control,
    setValue,
    formState: { errors }
  } = useForm<FormAddDocument>({ resolver: yupResolver(FormAddDocumentSchema) });

  const fileInputIconRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    setValue('file', fileList[0]);
  };
  const handleFileClick = useCallback(() => {
    if (fileInputIconRef.current) {
      fileInputIconRef.current.click();
    }
  }, []);

  const handleDeleteLogo = useCallback(() => {
    setValue('file', undefined);
  }, [setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);
  const fileDocument = watch('file');
  return (
    <DialogConfirm maxWidth={700} textTitle={'Add Document'} isOpen={isOpen} onClose={onCloseModal}>
      <Stack width={'100%'} alignItems={'flex-end'}>
        <Stack width={'100%'} alignItems={'flex-start'} gap={'20px'}>
          <Stack>
            <Typography fontSize={'16px'} mb={1} fontWeight={700} color={'#818181'}>
              Documents file
            </Typography>
            <Stack
              width={'100%'}
              minWidth={170}
              height={170}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const fileList = e.dataTransfer.files;
                if (fileList && fileList.length > 0) {
                  setValue('file', fileList[0]);
                }
              }}
              style={{
                backgroundColor: fileDocument ? '#fff' : isDragging ? '#e3f2fd' : '#D9D9D9',
                border: isDragging ? '2px dashed #1976d2' : 'none'
              }}
              alignItems={'center'}
              justifyContent={'center'}>
              <input
                accept="image/*, application/pdf"
                ref={fileInputIconRef}
                hidden
                onChange={(event) => handleFileChange(event)}
                type="file"
              />
              {fileDocument &&
                (fileDocument.type === 'application/pdf' ? (
                  <Stack gap={1} mt={2} alignItems={'center'} width={'100%'}>
                    <img
                      height={120}
                      style={{ objectFit: 'contain' }}
                      src={'/icons/pdf-icon.svg'}
                      alt={'icon'}
                    />
                    <Typography>{fileDocument.name}</Typography>
                  </Stack>
                ) : (
                  <img
                    height={80}
                    style={{ objectFit: 'contain' }}
                    src={URL.createObjectURL(fileDocument)}
                    alt={'icon'}
                  />
                ))}
              {fileDocument ? (
                <IconButton onClick={handleDeleteLogo}>
                  <img alt={'icon-user'} src={'/icons/icon_delete.svg'} width={24} />
                </IconButton>
              ) : (
                <IconButton onClick={handleFileClick}>
                  <Stack
                    alignItems={'center'}
                    justifyContent={'center'}
                    bgcolor={'#D9D9D9'}
                    padding={1.8}
                    sx={{ cursor: 'pointer' }}>
                    <img
                      width={53}
                      height={53}
                      alt={'icon-user'}
                      src={'/icons/icon_logo_upload.svg'}
                    />
                  </Stack>
                </IconButton>
              )}
            </Stack>
            {!fileDocument && errors.file?.message && (
              <TextError errorText={errors.file.message as string} />
            )}
          </Stack>
          <Stack style={{ width: '70%', gap: '20px' }}>
            <InputText
              inputError={errors.name?.message}
              placeholder={'Enter name'}
              title={'Name'}
              inputProps={{
                ...register('name')
              }}
            />
            {optionClient && (
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSelectMultiple
                    label={'Choose client'}
                    listChooseSelect={value || []}
                    placeholder={'Select Client'}
                    hasSelectAll
                    selectAllLabel={'All clients'}
                    onChangeInput={(val: string[]) => {
                      onChange(val);
                    }}
                    sx={{ padding: 0 }}
                    options={optionClient}
                  />
                )}
                name={'clientIds'}
              />
            )}
            {category && (
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSelect
                    title={'Choose Category'}
                    value={value}
                    placeholder={'Select Category'}
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      onChange(e);
                    }}
                    sx={{ padding: 0 }}
                    options={category}
                  />
                )}
                name={'category'}
              />
            )}
          </Stack>
        </Stack>
        <Stack mt={'30px'} flexDirection={'row'} gap={'20px'}>
          <ButtonOutLine
            label={'Cancel'}
            style={{ width: '166px' }}
            onClick={onCloseModal}
            fullWidth
          />
          <ButtonCustom
            label={'Add'}
            style={{ width: '166px' }}
            onClick={handleSubmit(onSubmit)}
            fullWidth
          />
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
